import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TownEyeLogo } from "@/components/TownEyeLogo";
import { PasswordField, passwordStrength } from "@/components/PasswordField";
import { CitySelect } from "@/components/CitySelect";
import { Loader2, Mail, Phone, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RegisterPage() {
  const { register, googleLogin, phoneRequestOtp, phoneVerifyOtp } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("email"); // email | phone

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-[var(--te-bg-soft)]">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6" data-testid="register-logo-link">
          <TownEyeLogo size={26} />
        </Link>

        <div
          className="bg-white rounded-[28px] border border-[var(--te-divider)] p-6 sm:p-8 te-fade-up"
          style={{ boxShadow: "0 24px 60px -20px rgba(103, 58, 183,0.15)" }}
        >
          <h1 className="font-extrabold text-3xl text-[var(--te-text)] text-center">
            Create your account
          </h1>
          <p className="text-sm text-[var(--te-text-secondary)] mt-2 text-center">Join your neighborhood in less than a minute.</p>

          {/* Auth method tabs */}
          <div className="mt-7 grid grid-cols-2 gap-1 bg-[var(--te-input-bg)] p-1 rounded-full">
            <button
              type="button"
              onClick={() => setMethod("email")}
              data-testid="register-tab-email"
              className={`text-sm font-semibold py-2 rounded-full flex items-center justify-center gap-1.5 transition-all ${
                method === "email" ? "bg-white text-[var(--te-primary)] shadow-sm" : "text-[var(--te-text-secondary)]"
              }`}
            >
              <Mail size={14} /> Email
            </button>
            <button
              type="button"
              onClick={() => setMethod("phone")}
              data-testid="register-tab-phone"
              className={`text-sm font-semibold py-2 rounded-full flex items-center justify-center gap-1.5 transition-all ${
                method === "phone" ? "bg-white text-[var(--te-primary)] shadow-sm" : "text-[var(--te-text-secondary)]"
              }`}
            >
              <Phone size={14} /> Phone
            </button>
          </div>

          {method === "email" ? (
            <EmailSignup register={register} googleLogin={googleLogin} onDone={() => navigate("/onboarding")} />
          ) : (
            <PhoneSignup phoneRequestOtp={phoneRequestOtp} phoneVerifyOtp={phoneVerifyOtp} onDone={() => navigate("/onboarding")} />
          )}

          <p className="mt-7 text-center text-sm text-[var(--te-text-secondary)]">
            Already a member?{" "}
            <Link to="/login" className="font-semibold text-[var(--te-primary)] hover:underline" data-testid="register-link-login">
              Sign in
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-[var(--te-text-muted)] mt-6 max-w-sm mx-auto leading-relaxed">
          We never share your data. Read our{" "}
          <Link to="/terms" className="underline hover:text-[var(--te-text)]">Terms</Link> and{" "}
          <Link to="/privacy" className="underline hover:text-[var(--te-text)]">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}

// -------- Email signup --------
function EmailSignup({ register, googleLogin, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", city_id: null });
  const [touched, setTouched] = useState({});
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onBlur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = "Your full name is required.";
    else if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email address.";
    const ps = passwordStrength(form.password);
    if (!form.password) e.password = "Password is required.";
    else if (form.password.length < 8) e.password = "Password must be at least 8 characters.";
    else if (ps.score < 3) e.password = "Use a mix of letters, numbers, and symbols.";
    if (!form.city_id) e.city_id = "Please select your city from the list.";
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0 && agreed;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true });
    if (!isValid) {
      if (!agreed) toast.error("Please accept the Terms of Service to continue.");
      return;
    }
    setBusy(true);
    try {
      await register({ name: form.name.trim(), email: form.email.trim(), password: form.password, city_id: form.city_id });
      toast.success("Welcome to TownEye!");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doGoogle = async () => {
    setBusy(true);
    try {
      const demoEmail = window.prompt("Continue with Google — enter your Google email", "");
      if (!demoEmail) { setBusy(false); return; }
      const demoName = window.prompt("Display name", demoEmail.split("@")[0]) || demoEmail.split("@")[0];
      await googleLogin({ email: demoEmail, name: demoName });
      toast.success("Signed in with Google");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={doGoogle}
        disabled={busy}
        data-testid="register-google-btn"
        className="mt-6 w-full flex items-center justify-center gap-3 border border-[var(--te-divider)] rounded-full py-3 px-6 hover:bg-[var(--te-input-bg)] transition-colors font-medium text-[var(--te-text)]"
      >
        <GoogleIcon />
        <span className="text-sm">Continue with Google</span>
      </button>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[var(--te-divider)]" />
        <span className="text-xs uppercase tracking-wider text-[var(--te-text-muted)]">or</span>
        <div className="flex-1 h-px bg-[var(--te-divider)]" />
      </div>

      <form onSubmit={submit} noValidate className="space-y-4">
        <Field
          label="Full name" value={form.name} onChange={onChange("name")} onBlur={onBlur("name")}
          error={touched.name && errors.name} placeholder="Alex Morgan" testId="register-name-input" autoComplete="name"
        />
        <Field
          label="Email" type="email" value={form.email} onChange={onChange("email")} onBlur={onBlur("email")}
          error={touched.email && errors.email} placeholder="you@example.com" testId="register-email-input" autoComplete="email"
        />
        <div>
          <PasswordField
            value={form.password}
            onChange={(v) => setForm((f) => ({ ...f, password: v }))}
            testId="register-password-input"
            label="Password"
            placeholder="At least 8 characters"
          />
          {touched.password && errors.password && (
            <p className="mt-1.5 text-xs text-[#DC2626]" data-testid="register-password-error">{errors.password}</p>
          )}
        </div>
        <div>
          <label className="te-label">City <span className="text-[#DC2626]">*</span></label>
          <CitySelect
            value={form.city_id}
            onChange={(id) => { setForm((f) => ({ ...f, city_id: id })); setTouched((t) => ({ ...t, city_id: true })); }}
            invalid={touched.city_id && !!errors.city_id}
            placeholder="Search city in Hebrew, Arabic, or English…"
            testId="register-city-select"
          />
          {touched.city_id && errors.city_id && (
            <p className="mt-1.5 text-xs text-[#DC2626]" data-testid="register-city-error">{errors.city_id}</p>
          )}
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer pt-1" data-testid="register-terms-label">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[var(--te-divider)] text-[var(--te-primary)] focus:ring-[var(--te-primary)] cursor-pointer"
            data-testid="register-terms-checkbox"
          />
          <span className="text-sm text-[var(--te-text-secondary)] leading-snug">
            I agree to the{" "}
            <Link to="/terms" target="_blank" rel="noopener" className="font-medium text-[var(--te-primary)] hover:underline">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" target="_blank" rel="noopener" className="font-medium text-[var(--te-primary)] hover:underline">Privacy Policy</Link>.
          </span>
        </label>

        <button
          type="submit"
          disabled={!isValid || busy}
          data-testid="register-submit-btn"
          className="te-btn-primary w-full !py-3.5 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? <><Loader2 size={16} className="animate-spin" /> Creating your account…</> : "Create account"}
        </button>
      </form>
    </>
  );
}

// -------- Phone signup --------
function PhoneSignup({ phoneRequestOtp, phoneVerifyOtp, onDone }) {
  const [stage, setStage] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const phoneValid = /^\+?[0-9\s\-()]{7,18}$/.test(phone);

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!phoneValid) { toast.error("Enter a valid phone number"); return; }
    if (!name.trim()) { toast.error("Full name is required"); return; }
    if (!agreed) { toast.error("Please accept the Terms of Service to continue."); return; }
    setBusy(true);
    try {
      const data = await phoneRequestOtp(phone.trim());
      toast.success(data.message || "OTP sent");
      if (data.dev_code) {
        toast(`Demo OTP: ${data.dev_code}`, { duration: 8000 });
      }
      setStage("otp");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4) { toast.error("Enter the OTP you received"); return; }
    setBusy(true);
    try {
      await phoneVerifyOtp({ phone: phone.trim(), code: otp.trim(), name: name.trim() });
      toast.success("Phone verified — welcome!");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (stage === "phone") {
    return (
      <form onSubmit={requestOtp} className="mt-6 space-y-4" noValidate>
        <Field
          label="Full name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Alex Morgan" testId="register-phone-name-input"
        />
        <Field
          label="Phone number" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 555 123 4567" testId="register-phone-input" autoComplete="tel"
        />

        <label className="flex items-start gap-2.5 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[var(--te-divider)] text-[var(--te-primary)] focus:ring-[var(--te-primary)]"
            data-testid="register-phone-terms-checkbox"
          />
          <span className="text-sm text-[var(--te-text-secondary)] leading-snug">
            I agree to the{" "}
            <Link to="/terms" target="_blank" rel="noopener" className="font-medium text-[var(--te-primary)] hover:underline">Terms of Service</Link>{" "}and{" "}
            <Link to="/privacy" target="_blank" rel="noopener" className="font-medium text-[var(--te-primary)] hover:underline">Privacy Policy</Link>.
          </span>
        </label>

        <button
          type="submit"
          disabled={!phoneValid || !name.trim() || !agreed || busy}
          data-testid="register-phone-send-otp-btn"
          className="te-btn-primary w-full flex items-center justify-center gap-2 py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? <><Loader2 size={16} className="animate-spin" /> Sending code…</> : "Send verification code"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verifyOtp} className="mt-6 space-y-4" noValidate>
      <button type="button" onClick={() => setStage("phone")} className="text-xs text-[var(--te-text-secondary)] flex items-center gap-1 hover:text-[var(--te-text)]" data-testid="register-phone-back-btn">
        <ArrowLeft size={12} /> Use a different number
      </button>
      <div className="flex items-center gap-2 text-sm text-[var(--te-text)] bg-[var(--te-input-bg)] rounded-xl px-4 py-3">
        <ShieldCheck size={16} className="text-[var(--te-primary)]" />
        Code sent to <span className="font-semibold">{phone}</span>
      </div>
      <Field
        label="6-digit code" inputMode="numeric" maxLength={6} value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
        placeholder="123456" testId="register-phone-otp-input"
      />
      <button
        type="submit"
        disabled={otp.length < 4 || busy}
        data-testid="register-phone-verify-btn"
        className="te-btn-primary w-full flex items-center justify-center gap-2 py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying…</> : "Verify & continue"}
      </button>
    </form>
  );
}

function Field({ label, value, onChange, onBlur, error, placeholder, type = "text", testId, autoComplete, optional, inputMode, maxLength }) {
  return (
    <div>
      <label className="te-label">
        {label} {optional && <span className="text-[var(--te-text-muted)] font-normal">(optional)</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        data-testid={testId}
        className={`te-input ${error ? "border-[#DC2626] focus:ring-[#DC2626]/40 focus:border-[#DC2626]" : ""}`}
      />
      {error && <p className="mt-1.5 text-xs text-[#DC2626]" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.1 29.3 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.1 29.3 3 24 3 16.3 3 9.7 7.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.9 26.7 37 24 37c-5.3 0-9.7-3.4-11.3-8L6 33.7C9.3 40.6 16.1 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.6l6.2 5.2C41.9 35.4 45 30.1 45 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
